import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { Domain, DomainConfigFormFields, DomainDeploymentInfo, DomainFormFields, DomainsQueryKeys } from '@/modules/Domains';

export const useGetAllDomainsQuery = (paginationOptions: AppPaginationOptions, search?: string) =>
  buildQuery<WithPagination<
    Pick<Domain,
      | 'id'
      | 'name'
      | 'isHttps'
      | 'wwwRedirect'
      | 'healthStatus'
      | 'deployStatus'
      | 'isConfigured'
      | 'isDeployed'
      | 'createdAt'
      | 'configurationStatus'
    >
  >, QueryError>([DomainsQueryKeys.GetAll, { ...paginationOptions, search }], async () => {
    const { data } = await apiExecutor.get<WithPagination<Domain>>(ApiRoutes.Domains, {
      params: {
        ...paginationOptions,
        search
      }
    });
    return data;
  });

export const useGetByIdDomainsQuery = (id: string) =>
  buildQuery<Domain, QueryError>([DomainsQueryKeys.GetOneById, id], async () => {
    const { data } = await apiExecutor.get<Domain>(ApiRoutes.DomainsById.replace(':domainId', id));
    return data;
  });

export const useCreateDomainsMutation = () =>
  buildMutation<
    Pick<
      Domain,
      | 'id'
      | 'name'
      | 'isHttps'
      | 'wwwRedirect'
      | 'healthStatus'
      | 'deployStatus'
      | 'isConfigured'
      | 'isDeployed'
      | 'createdAt'
      | 'configurationStatus'
    >[],
    QueryError,
    DomainFormFields
  >([DomainsQueryKeys.Create], async (postData: DomainFormFields) => {
    const { data } = await apiExecutor.post(ApiRoutes.Domains, {
      ...postData
    }, { params: { teamId: localStorage.getItem('currentTeamId') } });
    return data;
  });

export const useEditDomainsMutation = (id: string) =>
  buildMutation<
  Pick<
    Domain,
    | 'id'
    | 'name'
    | 'isHttps'
    | 'wwwRedirect'
    | 'healthStatus'
    | 'deployStatus'
    | 'isConfigured'
    | 'isDeployed'
    | 'createdAt'
    | 'configurationStatus'
    >,
    QueryError,
    Partial<DomainFormFields & { id: string }>
  >(
    [DomainsQueryKeys.Edit, id],
    async (postData) => {
      const { data } = await apiExecutor.patch(ApiRoutes.DomainsById.replace(':domainId', id), {
        ...postData,
        id
      });
      return data;
    }
  );

export const useDeleteDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, void>([DomainsQueryKeys.Delete, id], async () => {
    await apiExecutor.delete(ApiRoutes.DomainsById.replace(':domainId', id));
  });

export const useGetConfigDomainsQuery = (id: string) =>
  buildQuery<DomainDeploymentInfo, QueryError>([DomainsQueryKeys.GetConfig, id], async () => {
    const { data } = await apiExecutor.get<DomainDeploymentInfo>(ApiRoutes.DomainsByIdConfig.replace(':domainId', id));
    return data;
  });

export const useAttachConfigDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, DomainConfigFormFields>(
    [DomainsQueryKeys.AttachConfig, id],
    async (postData) => {
      const { data } = await apiExecutor.post<void>(
        ApiRoutes.DomainsByIdConfigAttach.replace(':domainId', id),
        { ...postData }
      );
      return data;
    }
  );

export const useDetachConfigDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, void>([DomainsQueryKeys.DetachConfig, id], async () => {
    await apiExecutor.post<void>(ApiRoutes.DomainsByIdConfigDetach.replace(':domainId', id));
  });

export const useUpdateConfigDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, DomainConfigFormFields>(
    [DomainsQueryKeys.UpdateConfig, id],
    async (postData) => {
      const { data } = await apiExecutor.patch<void>(
        ApiRoutes.DomainsByIdConfigAttach.replace(':domainId', id),
        { ...postData }
      );
      return data;
    }
  );

export const useDeployDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, void>([DomainsQueryKeys.Deploy, id], async () => {
    await apiExecutor.post<void>(ApiRoutes.DomainsByIdDeploy.replace(':domainId', id));
  });

export const useShutdownDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, void>([DomainsQueryKeys.Shutdown, id], async () => {
    await apiExecutor.post<void>(ApiRoutes.DomainsByIdShutdown.replace(':domainId', id));
  });

export const useBreadcrumbDomainsQuery = (id: string) =>
  buildQuery<string, QueryError>([DomainsQueryKeys.Breadcrumb, id], async () => {
    const { data } = await apiExecutor.get<string>(ApiRoutes.DomainsByIdBreadcrumb.replace(':domainId', id));
    return data;
  });

export const useAutocompleteAllDomainsQuery = (params: { query: string; includedIds?: string[]; excludedIds?: string[]; }) =>
  buildQuery<
    Pick<Domain, 'id' | 'name'>[], QueryError
  >([DomainsQueryKeys.AutocompleteAll, params], async () => {
    const { data } = await apiExecutor.get<Pick<Domain, 'id' | 'name'>[]>(ApiRoutes.DomainsAutocomplete, {
      params: {
        q: params.query,
        includedIds: params?.includedIds?.length ? String(params.includedIds) : undefined,
        excludedIds: params?.excludedIds?.length ? String(params.excludedIds) : undefined,
      }
    });
    return data;
  });

export const useCheckHealthDomainsMutation = (id: string) =>
  buildMutation<void, QueryError, void>([DomainsQueryKeys.CheckHealth, id], async () => {
    await apiExecutor.post(ApiRoutes.DomainsByIdCheckHealth.replace(':domainId', id));
  });

export const useConfigureMultiDomainsMutation = () =>
  buildMutation<
    void,
    QueryError,
    DomainConfigFormFields & { domainIds: string[] }
  >([DomainsQueryKeys.MultiConfiguration], async (values) => {
    await apiExecutor.post(ApiRoutes.DomainsMultiConfiguration, { ...values });
  });
